import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import './_styles_/Bidding.css';
import ApplyChangesButton from '../buttons/ApplyChangesButton';
import NumberField from '../fields/NumberField';
import useExtendedState from '../../hooks/useExtendedState';
import Constants from '../../utils/Constants';
import useExtendedMutation from '../../hooks/useExtendedMutation';
import {
    UPDATE_BIDDING_USER_CONFIGURATION,
    USER_CONFIGURATION_QUERY_NAME,
} from '../../graphql/userConfiguration';
import { biddingUserConfigurationType } from '../../types';
import { updateBiddingUserConfiguration } from '../../actions/userConfiguration';
import useMultiValidator from '../../hooks/useMultiValidator';
import {
    defaultPriceValidator,
    defaultQuantityValidator,
    priceStepValidator,
    quantityStepValidator,
} from '../../validators/userConfiguration';
import InfoPanel from '../InfoPanel';
import { OPEN } from '../../utils/BiddingTypes';
import { compositeValidator, wafValidator } from '../../hooks/validationSupport';

Bidding.propTypes = {
    biddingUserConfiguration: biddingUserConfigurationType.isRequired,
};

function Bidding(props) {
    const {
        biddingUserConfiguration,
    } = props;
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const isAllowedOpenBiddingType = biddingUserConfiguration.allowedBiddingTypes.indexOf(OPEN.serverType) !== -1;
    const defaultPriceState = useExtendedState(biddingUserConfiguration.defaultPrice);
    const defaultQuantityState = useExtendedState(biddingUserConfiguration.defaultQuantity);
    const priceStepState = useExtendedState(biddingUserConfiguration.priceStep);
    const quantityStepState = useExtendedState(biddingUserConfiguration.quantityStep);
    const isChanged = defaultPriceState.isChanged || defaultQuantityState.isChanged || priceStepState.isChanged || quantityStepState.isChanged;
    const { validators, isValid } = useMultiValidator(true, {
        defaultPrice: compositeValidator(defaultPriceValidator(t), wafValidator(t)),
        defaultQuantity: compositeValidator(defaultQuantityValidator(t, Constants.MIN_SMALLEST_UNIT), wafValidator(t)),
        priceStep: compositeValidator(priceStepValidator(t, Constants.MIN_PRICE_STEP), wafValidator(t)),
        quantityStep: compositeValidator(quantityStepValidator(t, Constants.MIN_SMALLEST_UNIT), wafValidator(t)),
    });
    const isChangedPromise = isChanged ? Promise.resolve() : Promise.reject();
    const [isDisabled, setIsDisabled] = useState(!isChanged);
    Promise.all([isChangedPromise, isValid()])
        .then(() => setIsDisabled(false))
        .catch(() => setIsDisabled(true));

    const successCallback = () => {
        dispatch(updateBiddingUserConfiguration(defaultPriceState.value, defaultQuantityState.value, priceStepState.value, quantityStepState.value));
        defaultPriceState.changeInitialState(defaultPriceState.value);
        defaultPriceState.reset();
        defaultQuantityState.changeInitialState(defaultQuantityState.value);
        defaultQuantityState.reset();
        priceStepState.changeInitialState(priceStepState.value);
        priceStepState.reset();
        quantityStepState.changeInitialState(quantityStepState.value);
        quantityStepState.reset();
    };
    const updateBiddingUserConfigurationMutation = useExtendedMutation(UPDATE_BIDDING_USER_CONFIGURATION, {
        variables: {
            defaultPrice: defaultPriceState.value,
            defaultQuantity: isAllowedOpenBiddingType ? defaultQuantityState.value : null,
            priceStep: priceStepState.value,
            quantityStep: isAllowedOpenBiddingType ? quantityStepState.value : null,
        },
        successCallback,
        successMessageOptions: { key: 'user-configuration.bidding.notification.update-success', dataProperty: 'updateBiddingUserConfiguration' },
        failureMessageOptions: { key: 'user-configuration.bidding.notification.update-failure' },
        refetchQueries: [USER_CONFIGURATION_QUERY_NAME],
    });
    return (
        <>
            <h4>{t('user-configuration.bidding.default-values')}</h4>
            <InfoPanel message={t('user-configuration.bidding.info-box')} />
            <div className="user-configuration--bidding--fields">
                <NumberField
                    label={t('user-configuration.bidding.default-price')}
                    state={defaultPriceState}
                    fractionDigits={2}
                    validator={validators.defaultPrice}
                    className="bidding--default-values--default-price"
                />
                {isAllowedOpenBiddingType && (
                    <NumberField
                        label={t('user-configuration.bidding.default-quantity')}
                        state={defaultQuantityState}
                        validator={validators.defaultQuantity}
                        min={Constants.MIN_SMALLEST_UNIT}
                        className="bidding--default-values--default-quantity"
                    />
                )}
                <NumberField
                    label={t('user-configuration.bidding.price-step')}
                    state={priceStepState}
                    fractionDigits={2}
                    validator={validators.priceStep}
                    min={Constants.MIN_PRICE_STEP}
                    className="bidding--default-values--price-step"
                />
                {isAllowedOpenBiddingType && (
                    <NumberField
                        label={t('user-configuration.bidding.quantity-step')}
                        state={quantityStepState}
                        validator={validators.quantityStep}
                        min={Constants.MIN_SMALLEST_UNIT}
                        className="bidding--default-values--quantity-step"
                    />
                )}
            </div>
            <div className="user-configuration-item--buttons">
                <ApplyChangesButton
                    extendedMutation={updateBiddingUserConfigurationMutation}
                    disabled={isDisabled}
                    confirmationMessage={t('user-configuration.bidding.confirm-update-bidding-defaults')}
                />
            </div>
        </>
    );
}

export default Bidding;
